import React, { useState } from 'react';
import { Customer, InvoiceGroup, BulkInvoicingSettings, BalanceGroupType, BALANCE_GROUPS } from '../../types/customer';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import { 
  ReceiptPercentIcon, 
  PlusIcon, 
  TrashIcon, 
  PencilIcon,
  DocumentTextIcon,
  CheckIcon,
  XMarkIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';

interface BulkInvoicingPanelProps {
  customer: Customer;
  onUpdate: (updatedCustomer: Partial<Customer>) => Promise<void>;
}

const BulkInvoicingPanel: React.FC<BulkInvoicingPanelProps> = ({ customer, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedPods, setSelectedPods] = useState<string[]>([]);
  const [invoicingMode, setInvoicingMode] = useState<'per_pod' | 'consolidated' | 'all_pods_single_invoice'>(
    customer.bulkInvoicingSettings?.invoicingMode || 'per_pod'
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);

  const allPods = [
    ...(customer.pod ? [{ ...customer.pod, isPrimary: true }] : []),
    ...(Array.isArray(customer.additionalPods) ? customer.additionalPods : []).map(pod => ({ ...pod, isPrimary: false }))
  ];

  const invoiceGroups = customer.invoiceGroups || [];

  const handleCreateGroup = () => {
    console.log('🆕 BulkInvoicingPanel: handleCreateGroup called', { newGroupName, selectedPods });
    if (!newGroupName.trim() || selectedPods.length === 0) {
      console.log('⚠️ BulkInvoicingPanel: Validation failed - missing name or pods');
      window.alert('Kérem adjon meg egy nevet és válasszon legalább egy POD-ot!');
      return;
    }

    // Remove selected PODs from other groups to prevent duplication
    const cleanedGroups = invoiceGroups.map(group => ({
      ...group,
      podIds: group.podIds.filter(podId => !selectedPods.includes(podId)),
      updatedAt: selectedPods.some(podId => group.podIds.includes(podId)) ? new Date() : group.updatedAt
    }));

    const newGroup: InvoiceGroup = {
      id: `ig_${Date.now()}`,
      name: newGroupName,
      podIds: selectedPods,
      createdAt: new Date()
    };

    const updatedGroups = [...cleanedGroups, newGroup];
    console.log('✅ BulkInvoicingPanel: Creating new group (PODs removed from other groups)', { newGroup, updatedGroups });
    
    onUpdate({
      invoiceGroups: updatedGroups
    });
    console.log('📤 BulkInvoicingPanel: onUpdate called for new group');

    setNewGroupName('');
    setSelectedPods([]);
    setIsEditing(false);
  };

  const handleDeleteGroup = (groupId: string) => {
    console.log('🗑️ BulkInvoicingPanel: handleDeleteGroup called', { groupId });
    setGroupToDelete(groupId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (!groupToDelete) return;
    
    const updatedGroups = invoiceGroups.filter(g => g.id !== groupToDelete);
    console.log('✅ BulkInvoicingPanel: Deleting group', { groupId: groupToDelete, updatedGroups });
    onUpdate({
      invoiceGroups: updatedGroups
    });
    console.log('📤 BulkInvoicingPanel: onUpdate called for delete');
    
    setDeleteConfirmOpen(false);
    setGroupToDelete(null);
  };

  const handleUpdateGroup = (groupId: string, updates: Partial<InvoiceGroup>) => {
    console.log('✏️ BulkInvoicingPanel: handleUpdateGroup called', { groupId, updates });
    
    // If updating podIds, remove those PODs from other groups first
    let cleanedGroups = invoiceGroups;
    if (updates.podIds) {
      cleanedGroups = invoiceGroups.map(g => {
        if (g.id === groupId) {
          return g; // Don't modify the group being updated yet
        }
        // Remove PODs that will be added to the updated group
        const filteredPodIds = g.podIds.filter(podId => !updates.podIds!.includes(podId));
        return {
          ...g,
          podIds: filteredPodIds,
          updatedAt: filteredPodIds.length !== g.podIds.length ? new Date() : g.updatedAt
        };
      });
    }
    
    const updatedGroups = cleanedGroups.map(g => 
      g.id === groupId ? { ...g, ...updates, updatedAt: new Date() } : g
    );
    console.log('✅ BulkInvoicingPanel: Updating group (PODs removed from other groups)', { groupId, updatedGroups });
    
    onUpdate({
      invoiceGroups: updatedGroups
    });
    console.log('📤 BulkInvoicingPanel: onUpdate called for update');
    
    setEditingGroupId(null);
  };

  const handleTogglePodInGroup = (podId: string) => {
    setSelectedPods(prev => 
      prev.includes(podId) 
        ? prev.filter(id => id !== podId)
        : [...prev, podId]
    );
  };

  const handleInvoicingModeChange = (mode: 'per_pod' | 'consolidated' | 'all_pods_single_invoice') => {
    console.log('🔄 BulkInvoicingPanel: handleInvoicingModeChange called', { from: invoicingMode, to: mode });
    setInvoicingMode(mode);
    const settings: BulkInvoicingSettings = {
      invoicingMode: mode
    };
    
    if (customer.bulkInvoicingSettings?.defaultInvoiceGroupId) {
      settings.defaultInvoiceGroupId = customer.bulkInvoicingSettings.defaultInvoiceGroupId;
    }
    console.log('✅ BulkInvoicingPanel: Changing invoicing mode', { settings });
    
    onUpdate({
      bulkInvoicingSettings: settings
    });
    console.log('📤 BulkInvoicingPanel: onUpdate called for mode change');
  };

  const handleBalanceGroupChange = (balanceGroup: BalanceGroupType | undefined) => {
    console.log('⚖️ BulkInvoicingPanel: handleBalanceGroupChange called', { balanceGroup });
    onUpdate({
      balanceGroup: balanceGroup
    });
    console.log('📤 BulkInvoicingPanel: onUpdate called for balance group change');
  };

  const getPodLabel = (pod: any) => {
    return pod.id || 'Nincs POD ID';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <ReceiptPercentIcon className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Tömeges számlázás beállítások</h3>
            <p className="text-sm text-gray-600">Gyűjtőszámlák és POD csoportosítás kezelése</p>
          </div>
        </div>
      </div>

      {/* Balance Group Selection */}
      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <div className="flex items-center">
            <ScaleIcon className="w-4 h-4 mr-1.5 text-gray-600" />
            Mérlegkör
          </div>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => handleBalanceGroupChange(undefined)}
            className={`p-2.5 rounded-lg border-2 transition-all ${
              !customer.balanceGroup
                ? 'border-gray-400 bg-gray-100'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-center mb-1">
              <XMarkIcon className={`w-4 h-4 ${
                !customer.balanceGroup ? 'text-gray-600' : 'text-gray-400'
              }`} />
            </div>
            <div className="text-xs font-medium text-gray-900">Nincs megadva</div>
            <div className="text-[10px] text-gray-500 mt-0.5">Mérlegkör nélkül</div>
          </button>
          
          <button
            type="button"
            onClick={() => handleBalanceGroupChange('MVM')}
            className={`p-2.5 rounded-lg border-2 transition-all ${
              customer.balanceGroup === 'MVM'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-center mb-1">
              <ScaleIcon className={`w-4 h-4 ${
                customer.balanceGroup === 'MVM' ? 'text-blue-600' : 'text-gray-400'
              }`} />
            </div>
            <div className="text-xs font-medium text-gray-900">MVM</div>
            <div className="text-[10px] text-gray-500 mt-0.5">{BALANCE_GROUPS.MVM.fullName}</div>
            <div className="text-[9px] text-gray-400 mt-0.5 font-mono">{BALANCE_GROUPS.MVM.code}</div>
          </button>
          
          <button
            type="button"
            onClick={() => handleBalanceGroupChange('EON')}
            className={`p-2.5 rounded-lg border-2 transition-all ${
              customer.balanceGroup === 'EON'
                ? 'border-orange-500 bg-orange-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-center mb-1">
              <ScaleIcon className={`w-4 h-4 ${
                customer.balanceGroup === 'EON' ? 'text-orange-600' : 'text-gray-400'
              }`} />
            </div>
            <div className="text-xs font-medium text-gray-900">EON</div>
            <div className="text-[10px] text-gray-500 mt-0.5">{BALANCE_GROUPS.EON.fullName}</div>
            <div className="text-[9px] text-gray-400 mt-0.5 font-mono">{BALANCE_GROUPS.EON.code}</div>
          </button>
        </div>
      </div>

      {/* Invoicing Mode Selection */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-3">Számlázási mód</label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            type="button"
            onClick={() => handleInvoicingModeChange('per_pod')}
            className={`p-4 rounded-lg border-2 transition-all ${
              invoicingMode === 'per_pod'
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-center mb-2">
              <DocumentTextIcon className={`w-6 h-6 ${invoicingMode === 'per_pod' ? 'text-indigo-600' : 'text-gray-400'}`} />
            </div>
            <div className="text-sm font-medium text-gray-900">POD-onként</div>
            <div className="text-xs text-gray-500 mt-1">Minden POD külön számlát kap</div>
          </button>
          
          <button
            type="button"
            onClick={() => handleInvoicingModeChange('all_pods_single_invoice')}
            className={`p-4 rounded-lg border-2 transition-all ${
              invoicingMode === 'all_pods_single_invoice'
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-center mb-2">
              <DocumentTextIcon className={`w-6 h-6 ${invoicingMode === 'all_pods_single_invoice' ? 'text-green-600' : 'text-gray-400'}`} />
            </div>
            <div className="text-sm font-medium text-gray-900">Minden POD egy számlán</div>
            <div className="text-xs text-gray-500 mt-1">Összes POD összevonva egy számlára</div>
          </button>
          
          <button
            type="button"
            onClick={() => handleInvoicingModeChange('consolidated')}
            className={`p-4 rounded-lg border-2 transition-all ${
              invoicingMode === 'consolidated'
                ? 'border-purple-500 bg-purple-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-center mb-2">
              <ReceiptPercentIcon className={`w-6 h-6 ${invoicingMode === 'consolidated' ? 'text-purple-600' : 'text-gray-400'}`} />
            </div>
            <div className="text-sm font-medium text-gray-900">Egyedi gyűjtőszámlák</div>
            <div className="text-xs text-gray-500 mt-1">POD-ok csoportosítva több számlára</div>
          </button>
        </div>
      </div>

      {/* Invoice Groups List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-semibold text-gray-900">
            Gyűjtőszámlák ({invoiceGroups.length})
          </h4>
          <button
            type="button"
            onClick={() => setIsEditing(!isEditing)}
            className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Új gyűjtőszámla
          </button>
        </div>

        {/* Create New Group Form */}
        {isEditing && (
          <div className="mb-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <h5 className="text-sm font-semibold text-gray-900 mb-3">Új gyűjtőszámla létrehozása</h5>
            
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Gyűjtőszámla neve</label>
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="pl. OMV Gyűjtőszámla 1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                POD-ok kiválasztása
                <span className="ml-2 text-xs text-amber-600">(A POD automatikusan eltávolításra kerül más gyűjtőszámlákról)</span>
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {allPods.map((pod) => {
                  const existingGroup = invoiceGroups.find(g => g.podIds.includes(pod.id));
                  return (
                    <div
                      key={pod.id}
                      onClick={() => handleTogglePodInGroup(pod.id)}
                      className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPods.includes(pod.id)}
                        onChange={() => {}}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 pointer-events-none"
                      />
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {getPodLabel(pod)}
                          {pod.isPrimary && (
                            <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                              Fő POD
                            </span>
                          )}
                          {existingGroup && (
                            <span className="ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                              📋 {existingGroup.name}
                            </span>
                          )}
                        </div>
                        {pod.address && (
                          <div className="text-xs text-gray-500">{pod.address}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCreateGroup}
                className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <CheckIcon className="w-4 h-4 mr-2" />
                Létrehozás
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setNewGroupName('');
                  setSelectedPods([]);
                }}
                className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors"
              >
                <XMarkIcon className="w-4 h-4 mr-2" />
                Mégse
              </button>
            </div>
          </div>
        )}

        {/* Existing Groups */}
        {invoiceGroups.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ReceiptPercentIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">Még nincsenek gyűjtőszámlák</p>
            <p className="text-xs mt-1">Kattintson az "Új gyűjtőszámla" gombra a létrehozáshoz</p>
          </div>
        ) : (
          <div className="space-y-3">
            {invoiceGroups.map((group) => (
              <div key={group.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                {editingGroupId === group.id ? (
                  // Edit mode
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gyűjtőszámla neve
                      </label>
                      <input
                        type="text"
                        defaultValue={group.name}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Gyűjtőszámla neve"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        POD-ok kiválasztása
                      </label>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {allPods.map((pod) => (
                          <label
                            key={pod.id}
                            className="flex items-center p-2 hover:bg-white rounded-lg cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={group.podIds.includes(pod.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                const updatedPodIds = group.podIds.includes(pod.id)
                                  ? group.podIds.filter(id => id !== pod.id)
                                  : [...group.podIds, pod.id];
                                handleUpdateGroup(group.id, { podIds: updatedPodIds });
                              }}
                              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                            />
                            <div className="ml-3 flex-1">
                              <div className="text-sm font-medium text-gray-900">{getPodLabel(pod)}</div>
                              {pod.address && (
                                <div className="text-xs text-gray-500">{pod.address}</div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (newGroupName.trim()) {
                            handleUpdateGroup(group.id, { name: newGroupName });
                          }
                          setEditingGroupId(null);
                          setNewGroupName('');
                        }}
                        className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                      >
                        <CheckIcon className="w-4 h-4 mr-2" />
                        Mentés
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingGroupId(null);
                          setNewGroupName('');
                        }}
                        className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        <XMarkIcon className="w-4 h-4 mr-2" />
                        Mégse
                      </button>
                    </div>
                  </div>
                ) : (
                  // View mode
                  <>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h5 className="text-sm font-semibold text-gray-900">{group.name}</h5>
                        <p className="text-xs text-gray-500 mt-1">
                          {group.podIds.length} POD hozzárendelve
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingGroupId(group.id);
                            setNewGroupName(group.name);
                          }}
                          className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Szerkesztés"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteGroup(group.id)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Törlés"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1">
                      {group.podIds.map((podId) => {
                        const pod = allPods.find(p => p.id === podId);
                        if (!pod) return null;
                        return (
                          <div key={podId} className="text-xs text-gray-600 bg-white px-3 py-2 rounded border border-gray-200">
                            <span className="font-mono">{podId}</span>
                            {pod.address && <span className="ml-2 text-gray-500">- {pod.address}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800">Gyűjtőszámla információk</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Egy POD több gyűjtőszámlához is hozzárendelhető</li>
                <li>A gyűjtőszámla neve alapján történik a csoportosítás</li>
                <li>A szerződés létrehozásakor meg kell adni a gyűjtőszámla nevét</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setGroupToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Gyűjtőszámla törlése"
        deleteMessage="Biztosan törli ezt a gyűjtőszámlát?"
        deleteWarning="Ez a művelet nem vonható vissza. A gyűjtőszámla törlése után a POD-ok nem lesznek csoportosítva."
      />
    </div>
  );
};

export default BulkInvoicingPanel;
